// Despachador único de "reagendar fecha" para el panel Movimientos Futuros de FlujoCajaV2 (Fase 1
// de "Futuros: reagendar", 2026-09-23 — click en la celda de fecha abre un calendario y reagenda esa
// fila, sin pasar por el kebab). Fuente única de la lógica (10.1): `esFechaReagendable` decide qué
// filas son clickeables, `reagendarEntryFuturo` despacha al WRITER EXISTENTE de cada tipo — nunca
// reimplementado, siempre el mismo util que ya usan los modales viejos del panel (PlanCuotasModal,
// ModificarFechaProyeccionCapexModal, ModificarFechaLoteCapexModal,
// ModificarFechaProyeccionCompromisoModal, EditarFechaRecurrenteModal, EditarFechaSueldoModal,
// EditarFechaCobroModal). Esos 8 modales NO cambian de comportamiento (siguen existiendo, siguen
// abriéndose desde el kebab/lápiz como hoy); esta es una SEGUNDA vía de escritura hacia el mismo
// destino — permanente, no deuda técnica (10.232), mientras cuotas de plan / boleta / factoring /
// residuos / impuestos / créditos / cartelería de gestión queden fuera de esta fase.
//
// Alcance de la Fase 1 (2026-09-23) — 11 categorías, todas SIN plan de cuotas activo:
//   1. Facturado (SII emitida) sin cuota, sin factoring   → guardarFechaFactura (tipo "cobro")
//   2. Proveedor SII recibida sin cuota                   → guardarFechaFactura (tipo "pago")
//   3. Proveedor externa sin cuota                        → guardarFechaFactura (tipo "pago")
//   4. CapEx (activo) sin plan                             → actualizarActivo
//   5. CapEx lote — línea FOB                              → guardarFechaLoteCapex
//   6. CapEx lote — línea componente                       → guardarFechaLoteCapex
//   7. Edificio (con plan_pagos_edificios.id real)          → actualizarPlanPagoEdificio
//   8. Compromiso sin plan                                  → actualizarCompromiso
//   9. Recurrente (ocurrencia de proveedor recurrente)      → setFechaPagoRecurrente
//  10. Sueldo líquido (solo gerentes)                       → setFechaPagoSueldo
//  11. Pipeline (Backlog): sales / contratos / cartelería
//      con línea de hardware (linea_id)                     → guardarFechaCobroPipeline
//
// Fase 2 (2026-09-24) agrega UNA categoría más — CUOTA de plan de factura (cobro o pago), la
// exclusión explícita #11 de la Fase 1 (`f.cuota_actual` no nulo) queda RETIRADA solo para este
// caso:
//  12. Cuota de plan_cobros_facturas (cobro, SII emitida)   → guardarFechaCuotaFactura
//  12b. Cuota de plan_pagos_facturas (pago: SII recibida,
//       externa o boleta — las tres cuelgan de esa tabla)   → guardarFechaCuotaFactura
//      El click mueve SOLO esa cuota puntual (por `cuota_id`) — nunca renumera `numero_cuota` ni
//      toca las cuotas hermanas del mismo plan; SE PERMITE que quede cronológicamente después de la
//      cuota siguiente (a diferencia del Lote CapEx, que sí valida orden). Editar el plan completo
//      sigue siendo exclusivo del kebab → PlanCuotasModal. Cuotas de CapEx y de compromiso quedan
//      FUERA de esta fase (fases futuras).
//
// Fase 3 (2026-09-25) agrega UNA categoría más — CUOTA de plan CapEx, la exclusión explícita de la
// Fase 2 ("Cuota de un plan de CapEx… queda FUERA", abajo) queda RETIRADA solo para CapEx (compromiso
// sigue fuera, fase futura):
//  13. Cuota de plan_pagos_activos (activo CON plan)         → guardarFechaCuotaActivo
//      El click mueve SOLO esa cuota puntual (por `cuota_id`), vía la RPC atómica
//      `reagendar_cuota_activo` (candado de fila real + lock del activo padre + trigger de estructura
//      sobre `fecha_vencimiento`, migración `20260924d_rpc_reagendar_cuota_activo.sql`, aplicada en
//      producción) — nunca renumera `numero_cuota` ni toca las cuotas hermanas del mismo plan; SE
//      PERMITE que quede cronológicamente después de la cuota siguiente (mismo criterio que la cuota
//      de factura, a diferencia del Lote CapEx). Editar el plan completo sigue siendo exclusivo del
//      kebab → "Editar plan de pagos" → `PlanPagosActivoModal` (RPC `guardar_plan_pagos_activo`, sin
//      cambios). Requiere `ctx.planesCargados === true` (mismo gate que ya regía el activo sin plan
//      desde Fase 1d) Y `cuota_tiene_evento_vigente` falso (evento vigente en `pagos_capex`, parcial
//      incluido, bloquea). Cuota de un plan de COMPROMISO sigue FUERA de esta fase.
//
// Fase 4 (2026-09-25, decisión de Ignacio) agrega UNA categoría más — boleta de honorarios SIN plan,
// la exclusión explícita de la Fase 2 ("Boleta de honorarios SIN plan… queda FUERA", abajo) queda
// RETIRADA:
//  14. Boleta de honorarios sin plan (`f.tipo === "proveedor" && f.es_boleta && f.cuota_actual==null`)
//      → guardarFechaFactura (tipo "pago"), tabla `boletas_honorarios`, columna `plazopago` (columna
//      nueva, migración `20260925c_boletas_plazopago.sql`, aplicada). Mismo patrón EXACTO que SII
//      recibida/externa sin plan — la única diferencia es que el id real de la fila
//      (`f.boleta_id`, numérico) viaja SEPARADO del `f.id` de la entry (el string sintético
//      `"boleta-<id>"` que arma el mapper como key de React, nunca resuelve a una fila real). Boleta
//      CON plan (con cuota) sigue reagendable desde la Fase 2 (categoría 12b de arriba), sin cambios.
//
// EXPLÍCITAMENTE FUERA (esFechaReagendable siempre false, sin writer):
//   - Factoring (giro/excedente): decisión de producto explícita.
//   - Cartelería de gestión/OPEX (sin linea_id, fee de gestión): decisión de producto explícita — el
//     writer (RPC actualizar_linea_facturacion) exige linea_id, no hay a dónde escribir.
//   - Residuos por proveedor, impuestos, créditos, cuotas de crédito: sin fila real que reagendar
//     (residuo) o sin decisión de producto para esta fase (impuestos/créditos).
//   - Cuota de un plan de COMPROMISO (`f.tipo === "compromiso" && f.tiene_plan`): decisión de producto
//     explícita — el calendario nuevo NUNCA debe poder tocar/borrar esos planes de cuotas,
//     responsabilidad exclusiva del modal viejo (CompromisoPagoModal). Fases futuras. Cuota de un plan
//     de CAPEX (`f.tipo === "capex" && f.tiene_plan`) SÍ es reagendable desde la Fase 3 (categoría 13
//     de arriba) — ya NO está en esta lista.
import { esGerente } from "../constants/permisos";
import { actualizarActivo } from "./activos";
import { actualizarCompromiso } from "./compromisosPago";
import { esCuotaReagendable } from "./cuotaReagendable";
import { guardarFechaCobroPipeline } from "./guardarFechaCobroPipeline";
import { guardarFechaCuotaActivo } from "./guardarFechaCuotaActivo";
import { guardarFechaCuotaFactura } from "./guardarFechaCuotaFactura";
import { guardarFechaFactura } from "./guardarFechaFactura";
import { guardarFechaLoteCapex } from "./guardarFechaLoteCapex";
import { setFechaPagoSueldo } from "./pagosEmpleados";
import { actualizarPlanPagoEdificio } from "./planPagosEdificios";
import { setFechaPagoRecurrente } from "./proveedoresRecurrentes";
import { MODOS_VALIDACION, validarFechaDocumento } from "./validarFechaDocumento";

// "Sueldo líquido" no es un `f.tipo` propio (vive en tipo:"rrhh"). Bono y comisión (decisión de
// Ignacio, Fase 1c) SÍ son reagendables y COMPARTEN el mismo override de empleado+período con el
// sueldo líquido — las tres filas son distintos conceptos de una misma cuenta corriente de empleado
// (`fechasPagoPorEmpMes`), así que mover cualquiera de las tres mueve el MISMO registro subyacente
// (mismo `setFechaPagoSueldo(empleado_id, periodo_año, periodo_mes, ...)`, sin distinguir concepto).
// PREVIRED y EsSalud NO son reagendables: FlujoCajaV2/index.js les aplica `camposFechaSueldo`
// únicamente a la fila base/bono/comisión, nunca a esas dos — por eso `es_sueldo_liquido` nunca les
// llega en `true` y quedan afuera sin necesidad de un exclude explícito acá. El flag `es_sueldo_liquido`
// distingue las tres reagendables de PREVIRED/EsSalud. Requiere ser gerente (esGerente) — mismo gate
// que ya usa el lápiz existente (5285: `f.es_sueldo_liquido && esGerente(userId)`).
const esSueldoReagendable = (f, ctx) => !!f?.es_sueldo_liquido && esGerente(ctx?.userId);

// Ocurrencia de proveedor recurrente: mismo boundary que `esRecurrenteEditable` en
// FlujoCajaV2/index.js (proveedor_id + período), reproducido acá porque ese predicado vive privado en
// el componente (no exportado) y crear un import cruzado componente↔util introduciría un ciclo (el
// componente importa este archivo). Si cambia el shape de las entries recurrentes en
// proveedoresRecurrentes.js, actualizar ambos.
const esRecurrenteReagendable = (f) =>
  !!f?.es_recurrente &&
  f.tipo === "proveedor" &&
  f.proveedor_id != null &&
  f.periodo_año != null &&
  f.periodo_mes != null;

// Cuota de plan de factura (cobro o pago) — Fase 2, extendida en Fase 2b. `cuota_id`/`cuota_estado`/
// `cuota_movimiento_bancario_id`/`cuota_tabla` los agrega el mapper de FlujoCajaV2/index.js (Fase 2,
// Commit 1) a partir de los MISMOS campos que ya trae el select de getCuotasCobrosFuturas/
// getCuotasPagosFuturas; `cuota_tiene_eventos`/`cuota_documento_tipo` los agrega el mismo mapper —
// `cuota_tiene_eventos` es `c._tiene_eventos` (Fase 2d, 2026-09-24: EXISTS puro — ¿esta cuota_id tiene
// AL MENOS una fila en el libro, sin importar el monto? — ya calculado por `getCuotasCobrosFuturas`/
// `getCuotasPagosFuturas` del MISMO array de eventos que ya cargan; reemplaza el `cuota_aplicado`/Σ-
// con-tolerancia de la Fase 2b, que era el único consumidor de ese campo, sin otros lectores);
// `cuota_documento_tipo` es el documento_tipo real de esa cuota según la rama del mapper que la generó
// (sii_emitida / sii_recibida / factura_externa / boleta_honorario).
// `typeof cuota_id === "number"` — mismo guard que `idEsEditable` de PlanCuotasModal (un id sintético
// no resuelve a una fila real). El resto del criterio ("cuota pendiente") vive en `esCuotaReagendable`
// (cuotaReagendable.js, 10.1) — MISMA fuente que usa el writer (`guardarFechaCuotaFactura`, vía la RPC
// `reagendar_cuota_factura` con su propio EXISTS dentro de la transacción) y que reusa
// `guardarFechaFactura.js` para decidir si borra el plan al pasar a pago único — ya NO una lista negra
// propia de este archivo (Fase 2 original: `cuota_estado !== "pagado" && cuota_estado !== "cobrado" &&
// cuota_movimiento_bancario_id == null`, RETIRADA). El chequeo REAL (candado de fila real dentro de la
// RPC, con su propio EXISTS del libro y su propia resolución de documento_tipo) vive en la RPC — este
// predicado es solo el gate de UI/despacho (defensa en profundidad con los DATOS que ya trajo el
// loader, nunca la única barrera).
const esCuotaFacturaReagendable = (f) =>
  typeof f?.cuota_id === "number" &&
  esCuotaReagendable({
    estado: f?.cuota_estado,
    movimiento_bancario_id: f?.cuota_movimiento_bancario_id,
    tieneEventos: f?.cuota_tiene_eventos,
    documento_tipo: f?.cuota_documento_tipo,
  });

/**
 * Predicado único: ¿esta fila del panel Futuros se puede reagendar haciendo click en su celda de
 * fecha? `ctx` es opcional — solo lo necesita el caso "sueldo líquido" (esGerente) y capex/compromiso
 * (planesCargados). CERRADO POR DEFECTO (Fase 1d, decisión de Ignacio): sin ctx, sueldo se trata como
 * no-reagendable (default seguro, nunca se asume gerente) Y capex/compromiso TAMBIÉN se bloquean
 * (`ctx.planesCargados` debe ser `true` EXACTO, no alcanza con cualquier valor truthy) — el caller
 * real, CeldaFechaReagendable vía FlujoCajaV2/index.js, siempre pasa `ctx.planesCargados` como
 * booleano explícito, así que el default cerrado solo importa para callers directos sin ctx (tests).
 * @param {Object} f - la entry de `proyeccionesFuturas`.
 * @param {Object} [ctx] - { userId, planesCargados }.
 */
export const esFechaReagendable = (f, ctx = {}) => {
  if (!f) return false;

  if (esSueldoReagendable(f, ctx)) return true;

  switch (f.tipo) {
    case "facturado":
      // Con cuota (cuota_actual no nulo, Fase 2): reagendable si la cuota releída no está
      // pagada/cobrada ni vinculada a un movimiento — nunca factoring (ninguna cuota de factoring
      // existe hoy, pero el guard queda explícito por si cambia el modelo).
      if (f.cuota_actual != null) return !f.es_factoring && esCuotaFacturaReagendable(f);
      // Sin cuota (mapFacturado no setea cuota_actual) y sin factoring (giro/excedente, es_factoring).
      // `typeof f.id === "number"` (Fase 1c, Commit 2b): mismo guard que `idEsEditable` de
      // PlanCuotasModal — un id sintético (string) no resuelve a una fila real, escribir con él
      // provoca 22P02 de Postgres o un match cruzado accidental.
      return typeof f.id === "number" && !f.es_factoring && f.cuota_actual == null;

    case "proveedor":
      if (f.es_recurrente) return esRecurrenteReagendable(f);
      // Con cuota (cuota_actual no nulo, Fase 2): cubre las TRES variantes del lado pago (SII
      // recibida, externa Y boleta — las tres cuelgan de plan_pagos_facturas y el mapper de
      // FlujoCajaV2/index.js les agrega los mismos 4 campos de identidad de cuota). Boleta CON cuota
      // pasa a ser reagendable acá, ANTES del `f.es_boleta` de abajo.
      if (f.cuota_actual != null) return esCuotaFacturaReagendable(f);
      // Residuo (sin fila real) queda fuera siempre — nunca tuvo un id real que reagendar.
      if (f.es_residuo) return false;
      // Boleta SIN plan (Fase 4 de "Futuros: reagendar", 2026-09-25 — decisión de Ignacio: se
      // reagenda igual que una factura SII sin plan, vía `boletas_honorarios.plazopago`, columna
      // nueva). `boleta_id` numérico (NO `f.id`, que es el string sintético `"boleta-<id>"` que arma
      // el mapper como key de React — `typeof f.id === "number"` de abajo jamás pasaría para esta
      // fuente). `boleta_id` viene de `camposModalBoleta` (mapper, FlujoCajaV2/index.js), el mismo
      // campo que ya usa el modal "Plan de cuotas".
      if (f.es_boleta) return typeof f.boleta_id === "number";
      // `typeof f.id === "number"` — mismo motivo que "facturado".
      return typeof f.id === "number" && f.cuota_actual == null;

    case "capex":
      // Cubre activo simple, lote FOB (es_lote sin costo_id), componente de lote (es_lote+costo_id) y
      // — desde la Fase 3 (2026-09-25) — la CUOTA de un activo CON plan (`f.tiene_plan`, agregada por
      // `mapCuotaActivo` en FlujoCajaV2/index.js). El gate `ctx.planesCargados` sigue siendo la primera
      // barrera para TODO el tipo "capex" (activo simple, lote y cuota por igual): `tiene_plan` depende
      // de `activosConPlan.has(a.id)` (FlujoCajaV2/index.js) — si esa carga falló, el Set queda
      // vacío/null y CUALQUIER activo (tenga o no plan real) se vería como "sin plan". CERRADO POR
      // DEFECTO (Fase 1d, decisión de Ignacio — endurece Fase 1c Commit 2a): `ctx.planesCargados !==
      // true` (comparación ESTRICTA, no truthy) bloquea — solo `true` exacto abre la puerta. `undefined`
      // (ctx ausente, `{}`, o cualquier valor no-`true`) bloquea también, a diferencia de antes
      // (`=== false`, donde `undefined` colaba). El único caller real (`ctxReagendarFuturo` en
      // FlujoCajaV2/index.js) siempre pasa un booleano explícito (`activosConPlan != null &&
      // compromisosConPlan != null`), así que este endurecimiento no cambia el comportamiento en
      // producción — solo cierra el default inseguro para callers sin ctx.
      if (ctx.planesCargados !== true) return false;
      if (f.tiene_plan) {
        // Fase 3: además del gate de arriba, la CUOTA puntual necesita `cuota_id` numérico real (mismo
        // guard `typeof === "number"` que `esCuotaFacturaReagendable` — el `f.id` compuesto
        // `capex-cuota-${id}`, NO `f.cuota_id`, es un string sintético que no resuelve a una fila real)
        // Y sin evento vigente en `pagos_capex` (`f.cuota_tiene_evento_vigente`, calculado por
        // `getCuotasActivosFuturas`/`mapCuotaActivo` del MISMO Map de `getSaldosCapexPorTarget` que ya
        // trae para el saldo — sin fetch adicional; un pago PARCIAL también bloquea, mismo criterio
        // EXISTS que usa la RPC `reagendar_cuota_activo` dentro de su propia transacción, decisión ya
        // cerrada por Ignacio). No hay concepto de "estado pendiente" acá — 10.258 retiró el flag
        // `estado` de `plan_pagos_activos`, el saldo/evento del ledger YA es el único criterio.
        // Fase 3b (auditoría independiente, M1, 2026-09-25): `=== false` ESTRICTO, no `!valor` — defensa
        // en profundidad complementaria al fix de loader de FlujoCajaV2/index.js (que ahora nunca deja
        // `cuotasActivosPendientes`/`saldosCapex` en un default vacío que enmascare una carga fallida).
        // Con `!valor`, un `cuota_tiene_evento_vigente` ausente/`undefined` (fila de forma inesperada,
        // bug de mapper, dato stale) colaba como "sin evento" → reagendable; con `=== false` SOLO un
        // `false` explícito (el mapper lo calculó de verdad) abre la puerta — `undefined` bloquea (10.177).
        return typeof f.cuota_id === "number" && f.cuota_tiene_evento_vigente === false;
      }
      // Activo simple sin plan, o línea de lote (nunca trae `tiene_plan`) — sin cambios de la Fase 1.
      return true;

    case "edificio":
      // Solo la línea con plan real (plan_pagos_edificios.id). El residual sintético (sin planId)
      // no tiene fila que reagendar.
      return f.planId != null;

    case "compromiso":
      // Mismo guard que "capex", cerrado por defecto — ver comentario de arriba (comparten
      // `ctx.planesCargados`, misma comparación estricta `!== true`).
      if (ctx.planesCargados !== true) return false;
      return !f.tiene_plan;

    case "pipeline":
      if (f.origen === "sales" || f.origen === "contratos") return true;
      // Cartelería: SOLO el tramo de hardware con línea derivada (linea_id + negocio_id). La cuota de
      // gestión/OPEX (sin linea_id) no tiene RPC a la que escribir.
      if (f.origen === "carteleria") return f.linea_id != null && f.negocio_id != null;
      return false;

    default:
      return false;
  }
};

/**
 * Reagenda la fecha de una fila del panel Futuros, despachando al writer existente de su tipo.
 * Valida `nuevaFecha` con `validarFechaDocumento` en modo ESTRICTO (piso `hoyDelPais(ctx.pais)` + día
 * hábil, Fase 5 de "Futuros: reagendar" — antes vivía inline acá, ahora es el validador único que
 * comparten los 16 caminos de fecha del sistema, 10.1) como segundo guard, independiente del
 * `minDate`/`filterDate` del calendario (decisión de producto: doble guard, nunca confiar solo en
 * la UI).
 *
 * @param {Object} f - la entry de `proyeccionesFuturas`.
 * @param {string} nuevaFecha - "YYYY-MM-DD".
 * @param {Object} [ctx] - { pais, userId }.
 * @returns {Promise<Object>} patch normalizado — ver cada rama para su forma exacta. El caller
 *   (CeldaFechaReagendable → FlujoCajaV2) lo usa para el patch puntual del estado local (10.234).
 * @throws {Error} para cualquier tipo no soportado o fecha inválida — NUNCA un return silencioso (10.177).
 */
export const reagendarEntryFuturo = async (f, nuevaFecha, ctx = {}) => {
  if (!f) throw new Error("reagendarEntryFuturo: falta la fila a reagendar.");
  if (!nuevaFecha) throw new Error("Seleccioná una fecha.");
  const validacion = validarFechaDocumento({ nuevaFecha, pais: ctx.pais, modo: MODOS_VALIDACION.ESTRICTO });
  if (!validacion.ok) {
    throw new Error(validacion.motivo);
  }
  if (!esFechaReagendable(f, ctx)) {
    // 10.177: nunca un no-op silencioso. Si esFechaReagendable dio false, nadie debería haber
    // llegado a llamar esta función (CeldaFechaReagendable la gatea) — si pasa, es un bug a corregir,
    // no un caso a tragar.
    throw new Error(`Esta fila (tipo="${f.tipo}") no admite reagendado de fecha en esta fase.`);
  }

  if (esSueldoReagendable(f, ctx)) {
    if (f.empleado_id == null || f.periodo_año == null || f.periodo_mes == null) {
      throw new Error("Fila de sueldo sin empleado/período — no se puede reagendar.");
    }
    await setFechaPagoSueldo(f.empleado_id, f.periodo_año, f.periodo_mes, nuevaFecha);
    return { _patchType: "sueldo" };
  }

  switch (f.tipo) {
    case "facturado": {
      // Cuota de plan_cobros_facturas (Fase 2): despacha al writer de UNA cuota, nunca al de la
      // factura completa — `esFechaReagendable` ya garantizó `f.cuota_id` numérico y la cuota sin
      // pagar/vincular (chequeo de UI); `guardarFechaCuotaFactura` relee de DB antes de escribir.
      if (f.cuota_id != null) {
        // Fase 2b: la tabla correcta la FIJA el tipo de entry ("facturado" → siempre
        // plan_cobros_facturas), nunca `f.cuota_tabla` (dato de UI) como única fuente — ese valor
        // viaja aparte, solo como verificación cruzada defensiva (`guardarFechaCuotaFactura` lanza
        // explícito si no coincide con lo que este tipo de entry dicta).
        const resultado = await guardarFechaCuotaFactura({
          cuota_id: f.cuota_id,
          cuota_tabla: "plan_cobros_facturas",
          cuotaTablaOrigen: f.cuota_tabla,
          nuevaFecha,
          pais: ctx.pais,
        });
        if (resultado.error) throw new Error(resultado.error);
        // Fase 3c (2026-09-25): `resultado.cuota` es la fila COMPLETA que devuelve la RPC
        // (`RETURNING to_jsonb(tabla.*)`, migración 20260925b) — ya incluye `fecha_vencimiento_
        // original` capturada por el trigger DENTRO del mismo UPDATE, sin fetch adicional. Viaja acá
        // para que `patchReagendado.js` la copie al slice local y la cuota se vea azul/sin-color al
        // instante (misma fórmula `esFechaModificada` que usa el mapper, sin esperar un refetch).
        //
        // Auditoría 3b/3c/4, Commit 4a (2026-09-25): `cuota` viaja CRUDA (sin aplanar con `?? null`
        // acá) — el aplanado con fallback a `null` vivía en ESTA función y, si `resultado.cuota` venía
        // ausente por cualquier motivo (writer legacy, respuesta parcial), pisaba con un `null` firme el
        // `fecha_vencimiento_original` que la cuota ya tenía en el estado local. `patchReagendado.js`
        // decide el fallback correcto (`c.fecha_vencimiento_original`, el valor previo de la fila), no
        // un `null` a ciegas — nunca pisa el original con un dato ausente (10.177).
        return {
          _patchType: "cuota_cobro",
          cuota_id: f.cuota_id,
          nuevaFecha,
          cuota: resultado.cuota,
        };
      }
      // F1 (B.2b, 2026-09-26): ESTRICTO explícito — ya validado arriba (línea de precheck de este
      // despachador), redundante-pero-consistente con el candado que ahora exige el writer.
      const resultado = await guardarFechaFactura({
        factura: f,
        tipo: "cobro",
        nuevaFecha,
        modo: MODOS_VALIDACION.ESTRICTO,
        pais: ctx.pais,
      });
      if (resultado.error) throw new Error(resultado.error);
      return { _patchType: "facturado", id: f.id, plazocobro: nuevaFecha, _plazoOverride: true };
    }

    case "proveedor": {
      if (f.es_recurrente) {
        await setFechaPagoRecurrente(
          f.proveedor_id,
          f.periodo_año,
          f.periodo_mes,
          nuevaFecha,
          ctx.pais,
          ctx.userId,
          f.costo_asociado_id ?? null
        );
        return {
          _patchType: "recurrente",
          proveedor_id: f.proveedor_id,
          periodo_año: f.periodo_año,
          periodo_mes: f.periodo_mes,
          costo_asociado_id: f.costo_asociado_id ?? null,
          nuevaFecha,
          tieneOverride: true,
        };
      }
      // Cuota de plan_pagos_facturas (Fase 2): cubre SII recibida, externa Y boleta — las tres
      // cuelgan de la misma tabla. Mismo despacho que la rama "facturado" de arriba.
      if (f.cuota_id != null) {
        // Fase 2b: mismo criterio que la rama "facturado" de arriba — la tabla la fija el tipo de
        // entry ("proveedor" → siempre plan_pagos_facturas, cubre SII recibida/externa/boleta),
        // `f.cuota_tabla` viaja solo como verificación cruzada.
        const resultado = await guardarFechaCuotaFactura({
          cuota_id: f.cuota_id,
          cuota_tabla: "plan_pagos_facturas",
          cuotaTablaOrigen: f.cuota_tabla,
          nuevaFecha,
          pais: ctx.pais,
        });
        if (resultado.error) throw new Error(resultado.error);
        // Fase 3c (2026-09-25) / Commit 4a: ver comentario espejo en la rama "facturado" de arriba.
        return {
          _patchType: "cuota_pago",
          cuota_id: f.cuota_id,
          nuevaFecha,
          cuota: resultado.cuota,
        };
      }
      // SII recibida, externa o boleta (Fase 4, 2026-09-25) — guardarFechaFactura discrimina
      // internamente por factura.es_externa/factura.es_boleta. NUNCA llega acá una factura/boleta con
      // plan de cuotas activo: esFechaReagendable ya lo excluyó (f.cuota_actual == null), y
      // guardarFechaFactura además borraría ese plan si lo tuviera — pero solo si lo tuviera, lo cual
      // esFechaReagendable garantiza que no es el caso.
      const resultado = await guardarFechaFactura({
        factura: f,
        tipo: "pago",
        nuevaFecha,
        modo: MODOS_VALIDACION.ESTRICTO,
        pais: ctx.pais,
      });
      if (resultado.error) throw new Error(resultado.error);
      // Boleta: _patchType propio "proveedor_boleta" con `boleta_id` (el id REAL de
      // boletas_honorarios, para que patchReagendado.js matchee por ID — 10.7 — contra
      // boletasHonorariosPendientes; `f.id` es el string sintético "boleta-<id>", inútil para eso).
      if (f.es_boleta) {
        return { _patchType: "proveedor_boleta", boleta_id: f.boleta_id, plazopago: nuevaFecha };
      }
      return { _patchType: f.es_externa ? "proveedor_externa" : "proveedor_sii", id: f.id, plazopago: nuevaFecha };
    }

    case "capex": {
      if (f.es_lote) {
        const resultado = await guardarFechaLoteCapex({ entry: f, nuevaFecha });
        if (resultado.error) throw new Error(resultado.error);
        return f.costo_id
          ? { _patchType: "capex_lote_componente", costo_id: f.costo_id, fecha_pago: nuevaFecha }
          : { _patchType: "capex_lote_fob", lote_id: f.lote_id, fecha_planificada: nuevaFecha };
      }
      if (f.tiene_plan) {
        // Cuota de un plan CapEx (Fase 3, 2026-09-25): despacha al writer de UNA cuota, nunca al de la
        // línea única del activo — `esFechaReagendable` ya garantizó `f.cuota_id` numérico y sin
        // evento vigente en `pagos_capex` (chequeo de UI); `guardarFechaCuotaActivo` (vía la RPC
        // `reagendar_cuota_activo`) relee de DB antes de escribir, dentro de su propia transacción.
        if (f.cuota_id == null) throw new Error("Fila de cuota CapEx sin cuota_id — no se puede reagendar.");
        const resultado = await guardarFechaCuotaActivo({ cuota_id: f.cuota_id, nuevaFecha, pais: ctx.pais });
        if (resultado.error) throw new Error(resultado.error);
        // `activo_id`/`fecha_planificada` vienen de la RESPUESTA de la RPC (paso 10 de la migración),
        // nunca inventados por el frontend — `fecha_planificada` puede venir distinta de `nuevaFecha`
        // si la cuota movida no era la más próxima del activo (el MIN no se mueve), o `null` en el
        // caso límite defensivo de un plan que quedó vacío (no debería pasar en un reagendado simple).
        return {
          _patchType: "cuota_activo",
          cuota_id: f.cuota_id,
          nuevaFecha,
          activo_id: resultado.activo_id,
          fecha_planificada: resultado.fecha_planificada,
        };
      }
      if (f.activo_id == null) throw new Error("Fila de CapEx sin activo_id — no se puede reagendar.");
      await actualizarActivo(
        f.activo_id,
        { fecha_planificada: nuevaFecha },
        { modo: MODOS_VALIDACION.ESTRICTO, pais: ctx.pais }
      );
      return { _patchType: "capex_activo", activo_id: f.activo_id, fecha_planificada: nuevaFecha };
    }

    case "edificio": {
      if (f.planId == null) throw new Error("Fila de edificio sin plan de pago real — no se puede reagendar.");
      // Solo fecha_acordada: `actualizarPlanPagoEdificio` hace un UPDATE parcial (spread de `cambios`),
      // así que el monto NO se toca (decisión de producto: el reagendado de fecha nunca cambia montos).
      const row = await actualizarPlanPagoEdificio(
        f.planId,
        { fecha_acordada: nuevaFecha },
        { modo: MODOS_VALIDACION.ESTRICTO, pais: ctx.pais }
      );
      return { _patchType: "edificio", planId: f.planId, row };
    }

    case "compromiso": {
      if (f.compromiso_id == null) throw new Error("Fila de compromiso sin compromiso_id — no se puede reagendar.");
      // Solo fecha_estimada — mismo motivo que edificio arriba, el monto actual queda intacto.
      await actualizarCompromiso(
        f.compromiso_id,
        { fecha_estimada: nuevaFecha },
        { modo: MODOS_VALIDACION.ESTRICTO, pais: ctx.pais }
      );
      return { _patchType: "compromiso", compromiso_id: f.compromiso_id, fecha_estimada: nuevaFecha };
    }

    case "pipeline": {
      await guardarFechaCobroPipeline({ f, nuevaFecha, userId: ctx.userId });
      return {
        _patchType: "pipeline",
        id: f.id,
        fecha_estimada: nuevaFecha,
        fecha_override_actual: nuevaFecha,
        tiene_override: true,
        // Fase 1d: `patchReagendado` ya no recibe el bundle de estado (que traía `estadoActual.pais`)
        // — el país necesario para convertir a CLP si la línea es en dólares viaja acá, en el propio
        // patch (mismo `ctx.pais` con el que se validó y escribió este reagendado).
        pais: ctx.pais,
      };
    }

    default:
      // Guard redundante con esFechaReagendable de arriba (defensivo — 10.177, nunca silencioso).
      throw new Error(`Esta fila (tipo="${f.tipo}") no admite reagendado de fecha en esta fase.`);
  }
};
